import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Segmented, Typography, Upload, message } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useNavigate } from 'react-router-dom';
import { FormSheet } from '../ui/FormSheet';
import {
  SUPPORT_FILE_COUNT,
  SupportCaseType,
  SupportPriority,
  isSupportUnavailable,
  supportFileError,
  supportService,
} from '../../services/supportService';

const { Text } = Typography;
const { TextArea } = Input;

const TYPE_OPTIONS: { label: string; value: SupportCaseType }[] = [
  { label: 'A problem', value: 'issue' },
  { label: 'A question', value: 'question' },
  { label: 'An idea', value: 'feature' },
];

const PRIORITY_OPTIONS: { label: string; value: SupportPriority }[] = [
  { label: 'Not blocking me', value: 'low' },
  { label: 'Getting in the way', value: 'normal' },
  { label: "I can't work", value: 'high' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  featureId?: string;
  initialType?: SupportCaseType;
}

export function ContactSupportSheet({ open, onClose, featureId, initialType }: Props) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [created, setCreated] = useState<{ ref: string; screenshotsFailed: boolean } | null>(null);

  useEffect(() => {
    if (!open) return;
    setIdempotencyKey(crypto.randomUUID());
    setFiles([]);
    setUnavailable(false);
    setCreated(null);
    form.setFieldsValue({
      type: initialType || 'issue',
      priority: 'normal',
      title: '',
      description: '',
    });
  }, [open, initialType, form]);

  const submit = async () => {
    const values = await form.validateFields();
    const chosen = files
      .map((item) => item.originFileObj)
      .filter((file): file is NonNullable<typeof file> => Boolean(file));
    for (const file of chosen) {
      const problem = supportFileError(file);
      if (problem) {
        message.error(problem);
        return;
      }
    }

    setSubmitting(true);
    setUnavailable(false);
    try {
      const result = await supportService.createCase({
        type: values.type,
        title: values.title.trim(),
        description: values.description.trim(),
        priority: values.priority,
        idempotencyKey,
        featureId,
        context: {
          url: window.location.href,
          appVersion: import.meta.env.VITE_APP_VERSION || undefined,
          userAgent: navigator.userAgent,
        },
      });

      let screenshotsFailed = false;
      if (chosen.length > 0 && result?.ref) {
        try {
          const uploads = [];
          for (const file of chosen) {
            const target = await supportService.requestUploadUrl(file);
            uploads.push(await supportService.uploadToS3(target, file));
          }
          await supportService.reply(result.ref, { body: 'Screenshots attached.', uploads });
        } catch (error) {
          screenshotsFailed = true;
          if (isSupportUnavailable(error)) setUnavailable(true);
        }
      }

      setCreated({ ref: result.ref, screenshotsFailed });
    } catch (error) {
      if (isSupportUnavailable(error)) setUnavailable(true);
      else message.error(error instanceof Error ? error.message : 'Could not open the case. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormSheet title="Contact support" open={open} onClose={onClose} width={720}>
      {created ? (
        <div>
          <Alert
            type="success"
            showIcon
            message={`Case ${created.ref} created`}
            description="We'll reply here and by email. You can add more detail from the case at any time."
          />
          {created.screenshotsFailed && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="The case is open, but the screenshots did not attach. Add them from the case."
            />
          )}
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <Button type="primary" onClick={() => { onClose(); navigate(`/support/${created.ref}`); }}>
              View case
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : (
        <Form form={form} layout="vertical" requiredMark={false} onFinish={submit}>
          {unavailable && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message="Support cases are not available right now."
              description={<a href="mailto:support@stagenote.ai">Email support@stagenote.ai</a>}
            />
          )}
          <Form.Item name="type" label="What's this about?">
            <Segmented options={TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, whitespace: true, message: 'Add a short title' }, { max: 200 }]}
          >
            <Input placeholder="Export spins forever" maxLength={200} />
          </Form.Item>
          <Form.Item
            name="description"
            label="What happened?"
            rules={[{ required: true, whitespace: true, message: 'Tell us what you saw' }, { max: 20000 }]}
          >
            <TextArea rows={6} placeholder="I clicked Export CSV on the invoices page and nothing downloaded." maxLength={20000} />
          </Form.Item>
          <Form.Item name="priority" label="How much is this blocking you?">
            <Segmented options={PRIORITY_OPTIONS} />
          </Form.Item>
          <Form.Item label="Screenshots" extra="Optional. Up to 5 files, 8 MB each.">
            <Upload
              multiple
              fileList={files}
              beforeUpload={(file) => {
                const problem = supportFileError(file);
                if (problem) {
                  message.error(problem);
                  return Upload.LIST_IGNORE;
                }
                if (files.length >= SUPPORT_FILE_COUNT) {
                  message.error('Attach up to 5 files.');
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={({ fileList }) => setFiles(fileList.slice(0, SUPPORT_FILE_COUNT))}
            >
              <Button icon={<PaperClipOutlined />}>Add a file</Button>
            </Upload>
          </Form.Item>
          <Text type="secondary">
            We'll include the page you are on and the app version, so we can see where this happened.
          </Text>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Send to support
            </Button>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          </div>
        </Form>
      )}
    </FormSheet>
  );
}
